'use client';

import { AuthProvider } from '@descope/nextjs-sdk';
import { useUser, useDescope, useSession } from '@descope/nextjs-sdk/client';
import type { ReactNode } from 'react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { getConfigValue } from '../../../../lib/public-env/get-config-value';
import { useElectronAuth } from '../../../../hooks/useElectronOpenIdAuth';
import { AuthContextProvider } from '../AuthContext';
import type { AuthContextValue, AuthProviderProps } from '../types';

const WebDescopeAuthContextBridge = ({ children }: { children: ReactNode }) => {
  const { user, isUserLoading } = useUser();
  const { isSessionLoading, isAuthenticated } = useSession();
  const descope = useDescope();

  const logout = useCallback(async () => {
    await descope.logout();
  }, [descope]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading: isUserLoading || isSessionLoading,
      user: user
        ? {
            userId: user.userId,
            email: user.email,
            name: user.name,
            picture: user.picture,
          }
        : null,
      logout,
      providerType: 'descope',
    }),
    [isAuthenticated, isUserLoading, isSessionLoading, user, logout],
  );

  return (
    <AuthContextProvider value={contextValue}>{children}</AuthContextProvider>
  );
};

const ElectronDescopeAuthContextBridge = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { contextValue } = useElectronAuth({ providerType: 'descope' });

  return (
    <AuthContextProvider value={contextValue}>{children}</AuthContextProvider>
  );
};

export function DescopeAuthProvider({ children }: AuthProviderProps) {
  const [isMounted, setIsMounted] = useState(false);
  const [isElectronRuntime, setIsElectronRuntime] = useState(false);
  const [projectId, setProjectId] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    setIsMounted(true);
    const hasElectronAuth = window.electronAPI?.auth !== undefined;
    setIsElectronRuntime(hasElectronAuth);

    if (hasElectronAuth) {
      return;
    }

    const descopeProjectId =
      getConfigValue('NEXT_PUBLIC_DESCOPE_PROJECT_ID') ?? '';
    const descopeBaseUrl =
      getConfigValue('NEXT_PUBLIC_DESCOPE_BASE_URL') ??
      getConfigValue('NEXT_PUBLIC_AUTH_BASE_URL') ??
      '';

    setProjectId(descopeProjectId);
    setBaseUrl(descopeBaseUrl);
  }, []);

  if (!isMounted) {
    return 'Authenticating...';
  }

  if (isElectronRuntime) {
    return (
      <ElectronDescopeAuthContextBridge>
        {children}
      </ElectronDescopeAuthContextBridge>
    );
  }

  if (!projectId) {
    return 'Authenticating...';
  }

  const refreshCookieName =
    getConfigValue('NEXT_PUBLIC_REFRESH_TOKEN_NAME') ?? 'DSR';

  return (
    <AuthProvider
      projectId={projectId}
      baseUrl={baseUrl}
      refreshCookieName={refreshCookieName}
    >
      <WebDescopeAuthContextBridge>{children}</WebDescopeAuthContextBridge>
    </AuthProvider>
  );
}
